import { useQuery } from '@apollo/client';
import IconButton from '@mui/material/IconButton';
import PrevIcon from '@mui/icons-material/KeyboardArrowLeft';
import NextIcon from '@mui/icons-material/KeyboardArrowRight';
import React from 'react';
import LazyLoad from 'react-lazyload';
import Carousel from 'react-multi-carousel';
import useWindowSize from 'react-use/lib/useWindowSize';
import { nameFromCC, slugFromCC } from 'tf-post-parser';
import { GET_RECOMMENDED_POSTS } from '../../helpers/graphql/posts';
import Link from '@mui/material/Link';
import BorderedText from './BorderedText';
import SimilarPostCard from './SimilarPostCard';

const SimilarPosts = props => {
  const { countryCode, coordinates, community } = props;

  const slug = slugFromCC(countryCode);

  const { width } = useWindowSize();

  const ButtonGroup = ({ next, previous, ...rest }) => {
    const {
      carouselState: { currentSlide },
    } = rest;
    return (
      <>
        <div
          className="d-absolute"
          style={{
            marginTop: -415,
            height: 407,
            marginLeft: -5,
          }}
        >
          <div className="d-flex align-items-center h-100 w-100">
            <div>
              {currentSlide === 0 ? (
                <></>
              ) : (
                <IconButton
                  color="primary"
                  onClick={() => previous()}
                  aria-label="Back"
                  size="large"
                >
                  <PrevIcon />
                </IconButton>
              )}
            </div>
          </div>
        </div>
        <div
          className="d-absolute"
          style={{
            marginTop: -407,
            height: 407,
          }}
        >
          <div className="d-flex justify-content-end h-100 align-items-center">
            <div>
              <IconButton
                color="primary"
                onClick={() => {
                  next();
                }}
                aria-label="Next"
                size="large"
              >
                <NextIcon />
              </IconButton>
            </div>
          </div>
        </div>
      </>
    );
  };

  const responsive = {
    retina: {
      breakpoint: { max: 10000, min: 2300 },
      items: 8,
    },
    extralargeDesktop: {
      breakpoint: { max: 2299, min: 2000 },
      items: 7,
    },
    largeDesktop: {
      breakpoint: { max: 1999, min: 1750 },
      items: 6,
    },
    desktop: {
      breakpoint: { max: 1749, min: 1470 },
      items: 5,
    },
    smallDesktop: {
      breakpoint: { max: 1469, min: 1190 },
      items: 4,
    },
    laptop: {
      breakpoint: { max: 1189, min: 920 },
      items: 3,
    },
    tablet: {
      breakpoint: { max: 919, min: 640 },
      items: 2,
    },
    mobile: {
      breakpoint: { max: 639, min: 0 },
      items: 1,
    },
  };

  let itemsToScroll = 1;
  if (width > 639) itemsToScroll = 2;
  if (width > 919) itemsToScroll = 3;
  if (width > 1189) itemsToScroll = 4;
  if (width > 1469) itemsToScroll = 5;
  if (width > 1749) itemsToScroll = 6;
  if (width > 1999) itemsToScroll = 7;
  if (width > 2299) itemsToScroll = 8;

  const { data } = useQuery(GET_RECOMMENDED_POSTS, {
    variables: {
      language: community ? community.lang : undefined,
      countryCode,
      nearCoordinates: coordinates,
      minScore: 9000,
      limit: 8,
      community: community && !coordinates ? community.id : undefined,
      orderby: community && !coordinates ? 'random' : undefined,
    },
  });

  if (data && data.posts && data.posts.length === 0) return <></>;
  return (
    <>
      <BorderedText>
        {countryCode ? (
          <Link href={`/destinations/${slug}`} color="primary">
            More from {nameFromCC(countryCode)}
          </Link>
        ) : (
          'Read Next'
        )}
      </BorderedText>
      <LazyLoad
        offset={1000}
        height={380}
        once
        placeholder={
          <Carousel
            ssr
            responsive={responsive}
            draggable={false}
            itemClass="p-2"
            slidesToSlide={itemsToScroll}
            arrows={false}
            customButtonGroup={<ButtonGroup />}
            renderButtonGroupOutside
            containerClass="tw-mx-8"
          >
            {[0, 1, 2, 3, 4, 5, 6, 7].map(holderId => {
              return (
                <div
                  className="d-flex justify-content-center"
                  key={`lazy-${holderId}`}
                >
                  <SimilarPostCard
                    cardHeight="200"
                    post={{ permlink: `lazypost-${holderId}` }}
                  />
                </div>
              );
            })}
          </Carousel>
        }
      >
        <Carousel
          ssr
          responsive={responsive}
          draggable={false}
          itemClass="p-2"
          slidesToSlide={itemsToScroll}
          arrows={false}
          customButtonGroup={<ButtonGroup />}
          renderButtonGroupOutside
          containerClass="tw-mx-8"
        >
          {data && data.posts && data.posts.length > 0
            ? data.posts.map(post => {
                const {
                  author,
                  permlink,
                  title,
                  thumb,
                  location,
                  youtubeId,
                } = post;
                return (
                  <SimilarPostCard
                    key={`${author.username}/${permlink}`}
                    slug={slug}
                    post={{
                      author: author.username,
                      permlink,
                      title,
                      thumb,
                      location,
                      isVideo: !!youtubeId,
                    }}
                    cardHeight="200"
                  />
                );
              })
            : [0, 1, 2, 3, 4, 5, 6, 7].map(holderId => {
                return (
                  <div
                    className="d-flex justify-content-center"
                    key={`lazy-${holderId}`}
                  >
                    <SimilarPostCard
                      cardHeight="200"
                      post={{ permlink: `lazypost-${holderId}` }}
                    />
                  </div>
                );
              })}
        </Carousel>
      </LazyLoad>
    </>
  );
};

export default SimilarPosts;
